import React, {
    useContext, useState, useCallback, useMemo, useEffect
} from 'react';
import _ from 'lodash';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { InlineLoader, Tooltip } from '@jutro/components';
import { useTranslator } from '@jutro/locale';
import { WizardContext, pageTemplateProps } from '@xengage/gw-portals-wizard-react';
import appConfig from 'app-config';
import { WizardStepProgressComponent } from 'ml-capability-quoteandbind-common-react';
import { MLAppStateContext } from 'ml-portals-util-react';
import { MLAlertGroupComponent } from 'ml-components-common-react';
import { MLTagManagerService} from 'ml-portals-services-react';
import { useAuthentication } from '@xengage/gw-digital-auth-react';
import { Button } from '@jutro/legacy/components';
import {MLQuoteFlowUtil, convertToSentenceCase} from "ml-portals-util-js";
import { platformMessages } from 'ml-components-platform-react';
import styles from './DigitalWizardPageTemplate.module.scss';
import WizardSidebar from '../components/WizardSidebar';
import MobileWizardSteps from '../components/MobileWizardSteps/MobileWizardSteps';
import PrivacyPolicyComponent from '../../../../capabilities-react/gw-capability-quoteandbind-common-react/components/PrivacyPolicy/PrivacyPolicyComponent';
import { Link } from '@jutro/router';
import { BreakpointTrackerContext } from '@jutro/legacy/layout';


function renderLoader(isLoading) {
    return isLoading ? <InlineLoader loading={isLoading} className="gw-inline-loader" /> : null;
}

function DigitalWizardPageTemplate(props) {
    const wizardContext = useContext(WizardContext);
    const {isSkipping} = wizardContext;
    const translator = useTranslator();
    const breakpoint = useContext(BreakpointTrackerContext);
    const { setAppState } = useContext(MLAppStateContext);
    const isPhone = breakpoint === 'phoneWide' || breakpoint === 'phone';
    const [showSidebar, setShowSidebar] = useState(false);
    const [isSkippingEmpDisc, setIsSkippingEmpDisc] = useState(false);
    const [contentKeyToggle_Ext, setContentKeyToggle_Ext] = useState(false);
    const { authHeader } = useAuthentication();


    const {
        children: content,
        onNext,
        isLoadingNext,
        disableNext,
        showNext,
        nextLabel,
        nextButtonTooltip,
        onPrevious,
        isLoadingPrevious,
        disablePrevious,
        showPrevious,
        previousLabel,
        onCancel,
        isLoadingCancel,
        disableCancel,
        showCancel,
        cancelLabel,
        renderContextComponent,
        renderWidgets,
        wizardData,
        wizardSnapshot,
        showContextComponent,
        showWizardHeader,
        customMessageModal,
        message,
        showTextNextToButton,
        textNextToButton,
        hidePrivacyPolicy,
        metadataPageId,
        overrideDivId,
        stickyComponentOverrideId,
        showReturnToQuote,
        disableReturnToQuote,
        updateWizardData,
        history,
        validateData,
        updateBeforeReturnToQuote,
        onReturnToQuote,
        showSkipEmployerDiscount
    } = props;

    useEffect(() => {
        switch (breakpoint) {
            case 'phoneWide':
            case 'phone':
                setShowSidebar(false);
                break;
            default:
                setShowSidebar(true);
                break;
        }
    }, [breakpoint]);

    useEffect(() => {
        setAppState({
            quoteID: _.get(wizardData, 'quoteID.value'),
            sessionUUID: _.get(wizardData, 'sessionUUID.value'),
            emailAddress1: _.get(wizardData, 'baseData.accountHolder.emailAddress1.value'),
            phoneNumber: _.get(wizardData, 'baseData.accountHolder.homeNumber.value'),
            submissionStatus: _.get(wizardData, 'baseData.status_ML.value'),
            policyNumber: _.get(wizardData, 'bindData.policyNumber.value')
        });
        MLTagManagerService.setModel(_.get(wizardData, 'submissionVM.value') || _.get(wizardData, 'value'));
        window.pageidScope = {
            'pfmLevel': _.get(wizardData, 'baseData.pfmLevel_ML.value') || '',
            'state': _.get(wizardData, 'baseData.policyAddress.state.value.code'),
            'postalCode': _.get(wizardData, 'baseData.policyAddress.postalCode.value')
        };
    }, [setAppState, wizardData])

    const { returnToQuoteScreen } = MLQuoteFlowUtil();
    const isQnB = appConfig.portalCode === 'QB';

    const toggleSideBar = useCallback(() => {
        setShowSidebar(!showSidebar);
    }, [showSidebar]);

    const contextComponent = useCallback(() => {
        return <div className={styles.wizardContext}>{renderContextComponent(wizardSnapshot)}</div>;
    }, [renderContextComponent, wizardSnapshot]);

    const shouldShowButtons = useMemo(() => !(
        showNext === false
        && showPrevious === false
        && showCancel === false
    ), [showCancel, showNext, showPrevious]);

    const isPerformingTransiton = useMemo(() => (
        isLoadingCancel || isLoadingPrevious || isLoadingNext
    ), [isLoadingCancel, isLoadingNext, isLoadingPrevious]);

    const wizardClassName = classNames(isQnB ? styles.wizardMainQnB : styles.wizardMain, {
        [styles.wizardMainShowSidebar]: showSidebar && isPhone
    });

    const shouldShowMessage = useMemo(() => {
        return !_.isEmpty(message);
    }, [message]);

    const nextButton = useMemo(() => (
        <Button
            id="gw-wizard-Next"
            className={isQnB ? styles.nextbuttonQnb : styles.nextbutton}
            disabled={disableNext || isPerformingTransiton}
            onClick={onNext}
        >
            {convertToSentenceCase(translator(nextLabel))}
            {renderLoader(isLoadingNext && !isSkippingEmpDisc)}

        </Button>
    ), [disableNext, isLoadingNext, isPerformingTransiton, nextLabel, onNext, translator,isSkippingEmpDisc]);

    const renderReturnToQuoteButton = useMemo(() => (
        <Button
            type="outlined"
            id="returnToQuoteButton"
            onClick={() => returnToQuoteScreen(validateData, wizardData, updateWizardData, history, authHeader, updateBeforeReturnToQuote, onReturnToQuote)}
            visible={false}
            disabled={disableReturnToQuote}
        >
            {translator(platformMessages.mlReturnToQuote)}
        </Button>
    ), [disableReturnToQuote, translator, returnToQuoteScreen, validateData, wizardData, updateWizardData, history, authHeader, updateBeforeReturnToQuote, onReturnToQuote]);

    const nextButtonWithTooltip = useMemo(() => (
        <Tooltip id="digitalWizardNextButtonTooltip" placement="bottom" content={nextButtonTooltip}>
            <span>{nextButton}</span>
        </Tooltip>
    ), [nextButton, nextButtonTooltip]);

    const renderNextButton = nextButtonTooltip ? nextButtonWithTooltip : nextButton;
    const buttonContainerClassName = classNames(
        styles.navTransitionButtons,
        isQnB && showSkipEmployerDiscount && styles.navTransitionButtonsWithSkip,
        {'navTransitionButtonsOverride': !!metadataPageId},
        {[styles.navTransitionButtonOnlyNext]: !showPrevious && isPhone}
    );

    const onSkipEmpDisc = useCallback(async () => {
        setIsSkippingEmpDisc(true);

        try{
            await onNext();
        } finally {
            setIsSkippingEmpDisc(false);
        }

    }, [onNext])

    const renderSkipEmployerDiscLink = useMemo(() => (
        <Link
            id="gw-wizard-SkipEmpDiscLink"
            className={styles.mlSkipEmpDiscLink}
            onClick={onSkipEmpDisc}
        >
            {convertToSentenceCase(translator(platformMessages.skipEmployerDiscount))}
            {renderLoader(isLoadingNext && isSkippingEmpDisc)}
        </Link>
    ), [isLoadingNext, isPerformingTransiton, nextLabel, onNext, translator,isSkippingEmpDisc]);

    const jobType = _.get(wizardData, 'submissionVM.baseData.jobType.value.code');
    const isPolicyChange = jobType === 'PolicyChange';

    const getMLTextNextToButtonOverride = () => {
        return (isQnB && 'mlTextNextToButtonOverrideQB') || (!isQnB && 'mlTextNextToButtonOverridePE');
    };

    const navButtonsDiv_Ext = () => {
        return (
            <div className={classNames(
                {[styles.navButtons]: shouldShowButtons},
                {'navButtonsOverride': shouldShowButtons && !!metadataPageId},
                'hiddenPrintElement'
            )}>
                {showCancel && (
                    <Button
                        id="gw-wizard-cancel"
                        disabled={disableCancel || isPerformingTransiton}
                        type="text"
                        onClick={onCancel}
                    >
                        {translator(cancelLabel)}
                        {renderLoader(isLoadingCancel)}
                    </Button>
                )}
                <div className={buttonContainerClassName}>
                    {showPrevious && (
                        <Button
                            id="gw-wizard-previous"
                            disabled={disablePrevious || isPerformingTransiton}
                            type="outlined"
                            onClick={onPrevious}
                        >
                            {translator(previousLabel)}
                            {renderLoader(isLoadingPrevious)}
                        </Button>
                    )}
                    {showReturnToQuote && renderReturnToQuoteButton}
                    {showNext && renderNextButton}
                    {isQnB && showSkipEmployerDiscount && renderSkipEmployerDiscLink}
                    {showTextNextToButton
                    && (
                        <div className={classNames(
                            styles.mlTextNextToButton,
                            isQnB && showSkipEmployerDiscount && styles.mlTextNextToButtonWithSkipLink,
                            {[getMLTextNextToButtonOverride()]: !!metadataPageId}
                        )}>
                            <span className="ml-cta-sub-footer__content">{translator(textNextToButton)}</span>
                        </div>
                    )}
                </div>
                {shouldShowMessage && (
                    <span className="ml-cta-sub-footer__content">{translator(message)}</span>
                ) }
            </div>
        );
    };

    useEffect(() => {
        setContentKeyToggle_Ext(!contentKeyToggle_Ext);
    }, [isLoadingNext]);

    const getContent_Ext = () => {
        if (metadataPageId) {
            const componentPage = content.flat().find((element) => {
                return _.get(element, 'props.uiProps.id') === metadataPageId;
            });
            if (componentPage) {
                if (stickyComponentOverrideId) {
                    _.set(componentPage, `props.overrideProps.${stickyComponentOverrideId}.navButtonsContent_Ext`, navButtonsDiv_Ext());
                }
                _.set(componentPage, `props.overrideProps.${overrideDivId}.content`, navButtonsDiv_Ext());
            }
        }
        return content;
    };

    return (
        <div className={(isSkipping) ? styles.loading : styles.digitalWizard}>
            {showContextComponent && contextComponent()}
            {/*    //</div><div className={wizardClassName}> */}
            { isQnB
                ? (
                    <header className="qb-progress-header__qbProgressHeader">
                        <WizardStepProgressComponent />
                    </header>
                )

                : (!isPhone || showSidebar) && (
                    <div className={classNames(styles.wizardNavigation, 'hiddenPrintElement')}>
                        <WizardSidebar customMessageModal={customMessageModal} />
                        {/* <div className={styles.wizardWidgets}>
                            {renderWidgets(wizardData)}
                        </div> */}
                    </div>
                )
            }
            { isQnB
                ? (<MLAlertGroupComponent />)
                : (<React.Fragment />)
            }
            <div className={isQnB ? styles.wizardMainQnB : styles.wizardMain}>
                <div className={styles.wizardContent}>
                    {(isPhone && !isQnB) ? <MobileWizardSteps toggleSideBar={toggleSideBar} /> : null}
                    {showWizardHeader && contextComponent()}
                    { isPolicyChange
                        ? (<MLAlertGroupComponent />)
                        : (<React.Fragment />)
                    }
                    <div key={contentKeyToggle_Ext}>
                        {getContent_Ext()}
                    </div>
                    {!metadataPageId && navButtonsDiv_Ext()}
                    {isQnB && !hidePrivacyPolicy && (
                        <PrivacyPolicyComponent />
                    )}
                </div>
            </div>
        </div>
    );
}


DigitalWizardPageTemplate.propTypes = {
    ...pageTemplateProps,
    /** a function that accepts wizardData to render a React component */
    renderContextComponent: PropTypes.func,
    /** a function that accepts wizardData to render a React component */
    renderWidgets: PropTypes.func,
    showContextComponent: PropTypes.bool,
    showWizardHeader: PropTypes.bool,
    nextButtonTooltip: PropTypes.string,
    showReturnToQuote: PropTypes.bool,
    disableReturnToQuote: PropTypes.bool,
    validateData: PropTypes.func,
    updateBeforeReturnToQuote: PropTypes.func,
    onReturnToQuote: PropTypes.func,
    showSkipEmployerDiscount: PropTypes.bool
};

DigitalWizardPageTemplate.defaultProps = {
    renderContextComponent: () => null,
    renderWidgets: () => null,
    showContextComponent: false,
    showWizardHeader: true,
    nextButtonTooltip: '',
    overrideDivId: 'footer',
    showReturnToQuote: false,
    disableReturnToQuote: true,
    validateData: () => true,
    showSkipEmployerDiscount: false
};

export default DigitalWizardPageTemplate;
